import { Autocomplete, Box, TextField, Divider, Typography } from "@mui/material";
import { colorSwapIconHoverSx } from "@/styles/iconHover";

const EMPTY_ARRAY = []

const CustomAutoComplete = ({
                                name,
                                label,
                                options,
                                value,
                                onChange,
                                textFieldValue,
                                customAutocomplete,
                                variant = "outlined",
                                limitTags = 1,
                                freeSolo = false,
                                valueField,
                                labelField,
                                multiple = true,
                                iconField,
                                disabled = false,
                                required = false,
                                onBlur = () => {},
                                helperText,
                                textFieldOnChange = () => {},
                                preventNetworkReset = false,

                                chipVariant = "default",
                                transparentBackground = false,
                                groupField,
                                readOnly = false,
                                borderRadius = 2,
                                ...props
                            }) => {

    const isTextControlled = textFieldValue !== undefined;

    const validOptions = options || EMPTY_ARRAY;

    const validValue = multiple
        ? (Array.isArray(value) ? value : EMPTY_ARRAY)
        : (value || null);

    const handleValueChange = (event, newValue) => {
        if (event) event.stopPropagation();

        if (onChange) {
            onChange(name, newValue);
        }

        if (isTextControlled) {
            if (multiple) {
                textFieldOnChange("");
            } else {
                let newText = "";
                if (newValue) {
                    if (typeof newValue === 'string') {
                        newText = newValue;
                    } else if (labelField && newValue[labelField]) {
                        newText = newValue[labelField];
                    }
                }
                textFieldOnChange(newText);
            }
        }
    };

    const isPill = chipVariant === "pill";
    const isTransparent = transparentBackground === true;

    return (
        <Autocomplete
            {...props}
            readOnly={readOnly}
            freeSolo={freeSolo}
            multiple={multiple}
            limitTags={limitTags}
            options={validOptions}
            value={validValue}
            disabled={disabled}
            filterSelectedOptions
            onChange={handleValueChange}
            onBlur={onBlur}
            {...(isTextControlled && {
                inputValue: textFieldValue || "",
                onInputChange: (event, newInputValue, reason) => {
                    if (reason === "input" || reason === "clear") {
                        textFieldOnChange(newInputValue);
                    }
                    if (reason === "reset") {
                        if (!preventNetworkReset || event) {
                            textFieldOnChange(newInputValue);
                        }
                    }
                }
            })}

            {...(groupField && {
                groupBy: (option) => (option && typeof option === 'object' ? option[groupField] : ""),
                renderGroup: (params) => (
                    <Box component="li" key={params.key} sx={{ listStyle: 'none' }}>
                        {params.group && (
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 1, px: 2, width: '100%' }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'text.secondary',
                                        mr: 1.5,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {params.group}
                                </Typography>
                                <Divider sx={{ flexGrow: 1 }} />
                            </Box>
                        )}
                        <Box component="ul" sx={{ padding: 0, margin: 0, listStyle: 'none' }}>
                            {params.children}
                        </Box>
                    </Box>
                )
            })}

            isOptionEqualToValue={(option, v) => {
                if (!v) return false;
                const isOptString = typeof option === 'string';
                const isValString = typeof v === 'string';
                const optId = isOptString ? option : (valueField && option ? option[valueField] : option);
                const valId = isValString ? v : (valueField && v ? v[valueField] : v);
                return String(optId) === String(valId);
            }}

            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return labelField && option?.hasOwnProperty?.(labelField) ? option[labelField] : "";
            }}

            renderOption={(props, option) => {
                const { key, ...rest } = props;
                const text = typeof option === 'string'
                    ? option
                    : (labelField ? option?.[labelField] : option);

                return (
                    <Box
                        key={"option_" + name + "_" + String(text) + "_" + Math.random()}
                        component={"li"}
                        display={"flex"}
                        alignItems={"center"}
                        {...rest}
                    >
                        {iconField ? option?.[iconField] : null}
                        {text}
                    </Box>
                );
            }}
            sx={{
                "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                    top: "50%",
                    transform: "translate(14px, -50%)",
                },
                "& .MuiInputBase-root": {
                    borderRadius,
                    minHeight: "37.13px",
                    paddingBlock: "0 !important",
                    backgroundColor: isTransparent ? "transparent" : undefined,
                },
                "& .MuiOutlinedInput-root": {
                    backgroundColor: isTransparent ? "transparent" : undefined,
                },
                ...(isTransparent
                    ? {
                        "& .MuiInputBase-input": { color: "#fff" },
                        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.85)" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
                        "& input::placeholder": { color: "rgba(255,255,255,0.65)", opacity: 1 },
                    }
                    : {}),
                ...(variant === "outlined"
                    ? {
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: isTransparent ? "rgba(255,255,255,0.35)" : undefined,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: isTransparent ? "rgba(255,255,255,0.6)" : undefined,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: isTransparent ? "rgba(255,255,255,0.9)" : undefined,
                        },
                    }
                    : {}),
                "& .MuiInputBase-input": {
                    padding: "0 !important",
                    boxSizing: "border-box",
                },
                ...(isPill
                    ? {
                        "& .MuiChip-root": {
                            borderRadius: 999,
                            height: 26,
                            backgroundColor: "transparent",
                            border: "2px solid rgba(255,255,255,0.9)",
                            color: "#fff",
                        },
                        "& .MuiChip-label": {
                            paddingInline: "10px",
                            paddingBlock: 0,
                            fontSize: "11px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                        },
                        "& .MuiChip-deleteIcon": {
                            margin: 0,
                            padding: 0,
                            ...colorSwapIconHoverSx({ color: "rgba(255,255,255,0.9)", hoverColor: "#fff" }),
                        },
                    }
                    : {
                        "& .MuiChip-root": { borderRadius: 4, height: "95%" },
                        "& .MuiChip-label": { paddingInline: "5px", paddingBlock: 0, fontSize: "10px" },
                        "& .MuiChip-deleteIcon": { margin: 0, padding: 0 },
                    }),
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant={variant}
                    required={required}
                    helperText={helperText}
                    onBlur={onBlur}
                    label={label}
                    placeholder={label}
                    size="small"
                    sx={{
                        ...(isTransparent
                            ? {
                                "& .MuiInputBase-root": { backgroundColor: "transparent" },
                            }
                            : {}),
                    }}
                />
            )}
        />
    );
};

export default CustomAutoComplete;